/**
 * ValueInput - Shared type-safe input components
 *
 * Supports 5 value types: String, Integer, Boolean, StringSet, IntegerSet
 *
 * Usage:
 *   // With type dispatcher
 *   <ValueInput type="String" value={value} onChange={setValue} />
 *
 *   // Or use individual components directly
 *   <StringInput value={value} onChange={setValue} options={["a", "b"]} />
 */

import {forwardRef} from "react";
import {unreachableCase} from "../../utils/typescript";
import BooleanInput from "./BooleanInput";
import IntegerInput from "./IntegerInput";
import IntegerSetInput from "./IntegerSetInput";
import StringInput from "./StringInput";
import StringSetInput from "./StringSetInput";
import {TypedValueInputProps} from "./types";

// Re-export types
export type {
  ValueType,
  ValueInputProps,
  ScalarValueInputProps,
  SetValueInputProps,
  TypedValueInputProps,
} from "./types";

// Re-export individual components
export {BooleanInput, IntegerInput, IntegerSetInput, StringInput, StringSetInput};

/**
 * Type dispatcher component - renders the appropriate input based on value type
 */
const ValueInput = forwardRef<HTMLInputElement, TypedValueInputProps>(
  ({type, value, onChange, options, placeholder, disabled}, ref) => {
    switch (type) {
      case "String":
        return (
          <StringInput
            ref={ref}
            value={value as string | null}
            onChange={onChange as (v: string | null) => void}
            options={options as string[] | undefined}
            placeholder={placeholder}
            disabled={disabled}
          />
        );
      case "Integer":
        return (
          <IntegerInput
            ref={ref}
            value={value as number | null}
            onChange={onChange as (v: number | null) => void}
            placeholder={placeholder}
            disabled={disabled}
          />
        );
      case "Boolean":
        return (
          <BooleanInput
            ref={ref}
            value={value as boolean | null}
            onChange={onChange as (v: boolean | null) => void}
            placeholder={placeholder}
            disabled={disabled}
          />
        );
      case "StringSet":
        return (
          <StringSetInput
            ref={ref}
            value={value as string[] | null}
            onChange={onChange as (v: string[] | null) => void}
            options={options as string[] | undefined}
            placeholder={placeholder}
            disabled={disabled}
          />
        );
      case "IntegerSet":
        return (
          <IntegerSetInput
            ref={ref}
            value={value as number[] | null}
            onChange={onChange as (v: number[] | null) => void}
            options={options as number[] | undefined}
            placeholder={placeholder}
            disabled={disabled}
          />
        );
      default:
        return unreachableCase(type);
    }
  },
);

ValueInput.displayName = "ValueInput";

export default ValueInput;
